import { forwardRef } from "react";
import { useRecoilValue } from "recoil";
import { Typography } from "../Shadcn/Typography";
import { cn } from "../../lib/utils";
import { hexLoadingState } from "../../states";

interface AggLayerOption {
    label: string;
    value: any;
    key: string;
    disabled?: boolean;
}

interface AggLayerPopupProps {
    options: AggLayerOption[];
    selectedKey: string;
    position: { top: number; left: number };
    onSelect: (value: any) => void;
}

export const AggLayerPopup = forwardRef<HTMLDivElement, AggLayerPopupProps>(({ options, selectedKey, position, onSelect }, ref) => {
    const hexLoading = useRecoilValue(hexLoadingState);

    const renderOptionContent = (option: AggLayerOption) => {
        const isHexGrid = option.key === 'hexgrid';
        const showLoader = isHexGrid && hexLoading.loading && !hexLoading.firstChunkReceived;
        const isHexDisabled = (isHexGrid && hexLoading.loading) || !!option.disabled;

        return {
            content: (
                <div className="flex items-center gap-[var(--semantic-xs)] w-full">
                    <Typography
                        variant="b2"
                        className="font-sans font-normal  leading-[var(--line-height-s-heading)] tracking-[var(--letter-spacing-default)] text-[var(--general-text-primary)]"
                    >
                        {option.label}
                    </Typography>
                    {showLoader && (
                        <span
                            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent shrink-0"
                            role="status"
                            aria-label="Loading hex data"
                        >
                            <span className="sr-only">Loading...</span>
                        </span>
                    )}
                </div>
            ),
            isDisabled: isHexDisabled
        };
    };

    return (
        <div
            ref={ref}
            className="flex flex-col items-start w-[9.2rem] py-[2px] px-[2px] gap-[2px] rounded-[var(--semantic-rounded-lg)]  bg-[var(--general-molecules-bg-bg-white)] fixed z-[9999]"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                boxShadow: 'var(--md-shadow-1-x, 0) var(--md-shadow-1-y, 4px) var(--md-shadow-1-blur, 6px) var(--md-shadow-1-spread, -1px) var(--md-color, rgba(0,0,0,0.10)), var(--md-shadow-2-x, 0) var(--md-shadow-2-y, 2px) var(--md-shadow-2-blur, 4px) var(--md-shadow-2-spread, -2px) var(--md-color, rgba(0,0,0,0.10))',
            }}
        >
            {options.map((option) => {
                const { content, isDisabled } = renderOptionContent(option);
                const isSelected = selectedKey === option.key;
                return (
                    <div key={option.key} className="flex flex-col self-stretch">
                        <label
                            className={cn(
                                "flex h-6 px-[var(--semantic-xs)] items-center gap-[var(--semantic-xs)] self-stretch",
                                isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-muted transition-colors"
                            )}
                        >
                            <input
                                type="radio"
                                name="aggLayerOption"
                                checked={isSelected}
                                onChange={() => !isDisabled && onSelect(option.value)}
                                disabled={isDisabled}
                                className="sr-only"
                            />
                            <div
                                className={cn(
                                    "shrink-0 size-[0.875rem] rounded-full border flex items-center justify-center",
                                    isSelected
                                        ? "border-[#c5c9d0] bg-white"
                                        : "border-[#c5c9d0] bg-transparent"
                                )}
                            >
                                {isSelected && <div className="size-[0.375rem] rounded-full bg-[var(--general-text-primary,#18202b)]" />}
                            </div>
                            {content}
                        </label>
                        {option.disabled && (
                            <div className="px-[var(--semantic-xs)] pl-7 opacity-60">
                                <Typography variant="b3" className="text-muted-foreground text-[0.6875rem]">
                                    Chosen data layer is not available at this unit.
                                </Typography>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
});
